import { useCallback, useState } from 'react';
import { apiKeyManager } from '@empiriecom/mybuy-session/ApiKey';
import type { LoginResponsePayload } from '@empiriecom/mybuy-frontend-api/backend-mybuy-customer/v1/models/LoginResponsePayload';
import type { LegacyCredentials } from '@empiriecom/mybuy-frontend-api/backend-mybuy-customer/v1/models/LegacyCredentials';
import type { CaptchaData } from '@empiriecom/mybuy-frontend-api/backend-mybuy-customer/v1/models/CaptchaData';
import { CaptchaDataFromJSON } from '@empiriecom/mybuy-frontend-api/backend-mybuy-customer/v1/models/CaptchaData';
import type { CustomerData } from '@empiriecom/mybuy-frontend-api/backend-mybuy-customer/v1';
import { CredentialsTypeEnum } from '@empiriecom/mybuy-frontend-api/backend-mybuy-customer/v1/models/Credentials';
import { useSWRConfig } from 'swr';
import type { GTMEventGlycerinDisplayFormSuccess } from '@packages/tracking/src/types/events';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import type { CustomerAddressWriteable } from '@packages/gql/generated/shopping/graphql';
import { useLoginApi } from './LoginApi';
import { useLogout } from './useLogout';
import type { PostLoginAction } from '../../PostLoginActions/postLoginActions';
import {
  postLoginActionsStorage,
  PostLoginActionType,
} from '../../PostLoginActions/postLoginActions';

export interface ShowCaptchaData {
  captchaData: CaptchaData | undefined;
  lastLogin: () => Promise<void>;
}

const noOp = async () => {};

/**
 * Converts API billingAddress to CustomerAddressWriteable type
 */
const convertToCustomerAddressWriteable = (
  billingAddress: CustomerData['billingAddress'],
): CustomerAddressWriteable | undefined => {
  if (!billingAddress) {
    return undefined;
  }

  return {
    additional: billingAddress.additional || null,
    addressName: billingAddress.addressName || null,
    birthName: billingAddress.birthName || null,
    city: billingAddress.city,
    countryCode: billingAddress.countryCode,
    firstName: billingAddress.firstName,
    gender: (billingAddress.gender as CustomerAddressWriteable['gender']) || null,
    honorific: billingAddress.honorific || null,
    houseNumber: billingAddress.houseNumber,
    lastName: billingAddress.lastName,
    primaryPhoneNumber: billingAddress.primaryPhoneNumber || null,
    secondaryPhoneNumber: billingAddress.secondaryPhoneNumber || null,
    street: billingAddress.street,
    zipCode: billingAddress.zipCode,
  };
};

export const useLogin = (onSuccess?: () => void) => {
  const { locale } = useI18n();
  const loginApi = useLoginApi();
  const { mutate } = useSWRConfig();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [customerData, setCustomerData] = useState<CustomerData>();
  const [showCaptchaState, setShowCaptchaState] = useState<ShowCaptchaData>();
  const { logout } = useLogout();
  const dispatchGTMEvent = useTracking();

  const removeCaptchaData = useCallback(() => {
    setShowCaptchaState({ lastLogin: showCaptchaState?.lastLogin || noOp, captchaData: undefined });
  }, [showCaptchaState]);

  // wrap loginFn so it can be reused after captcha check if needed
  const executeLogin = useCallback(
    async (loginFn: () => Promise<LoginResponsePayload>) => {
      removeCaptchaData();
      setLoading(true);
      setError(null);

      try {
        const result = await loginFn();
        if (result.errors && result.errors.elements?.length) {
          setError(result.errors.elements[0].description);
          return;
        }

        if (result.customer) {
          setCustomerData(result.customer);
        }

        // Collect post-login actions from backend flags
        const postLoginActions: PostLoginAction[] = [];

        if (result.warnings?.flags?.CheckBillingAddress && result.customer?.billingAddress) {
          const convertedAddress = convertToCustomerAddressWriteable(
            result.customer.billingAddress,
          );
          if (convertedAddress) {
            postLoginActions.push({
              type: PostLoginActionType.CHECK_BILLING_ADDRESS,
              timestamp: Date.now(),
              data: { billingAddress: convertedAddress },
            });
          }
        }

        if (result.warnings?.flags?.CheckEmailAddress) {
          postLoginActions.push({
            type: PostLoginActionType.CHECK_EMAIL,
            timestamp: Date.now(),
            data: { email: result.customer?.email },
          });
        }

        if (result.warnings?.flags?.TACReconfirmationRequired) {
          postLoginActions.push({
            type: PostLoginActionType.TAC_RECONFIRMATION,
            timestamp: Date.now(),
          });
        }

        if (result.tokens) {
          // Set tokens immediately - no more tmpToken!
          await apiKeyManager.setApiKeys(result.tokens, result.customer);
          mutate('session');

          // Add post-login actions to localStorage if any exist
          if (postLoginActions.length > 0) {
            postLoginActionsStorage.setActions(postLoginActions);
          }

          dispatchGTMEvent<GTMEventGlycerinDisplayFormSuccess>({
            event: 'DisplayFormSuccess',
            DisplayFormSuccessData: {
              category: 'login-form',
            },
          });

          onSuccess && onSuccess();
        }
      } catch (response: any) {
        if (response && response.status === 503) {
          const captchaDataFromJSON = CaptchaDataFromJSON(await response.json());
          setShowCaptchaState({
            captchaData: captchaDataFromJSON,
            lastLogin: () => executeLogin(loginFn),
          });
        } else {
          // Response is thrown from generated api - I don't want to break this behaviour
          /* istanbul ignore next */ throw response;
        }
      } finally {
        // prevent flashing of dialog after loading is finished
        setTimeout(() => {
          setLoading(false);
        }, 250);
      }
    },
    [removeCaptchaData, mutate, dispatchGTMEvent, onSuccess],
  );

  const login = ({
    email,
    password,
    remember,
  }: {
    email: string;
    password: string;
    remember?: boolean;
  }) => {
    if (!loginApi) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.error('[useLogin] loginApi not initialized when login was attempted');
      }
      setError('Login is temporarily unavailable. Please try again.');
      return;
    }

    executeLogin(() =>
      loginApi.login({
        ecLocale: locale,
        credentials: {
          type: CredentialsTypeEnum.Password,
          passwordCredentials: {
            email,
            password,
          },
          useAutologin: remember,
        },
      }),
    );
  };

  const legacyLogin = (legacyCredentials: LegacyCredentials, remember: boolean) => {
    if (loginApi) {
      executeLogin(() =>
        loginApi.login({
          ecLocale: locale,
          credentials: {
            type: CredentialsTypeEnum.Legacy,
            legacyCredentials,
            useAutologin: remember,
          },
        }),
      );
    }
  };

  return {
    loading,
    error,
    customer: customerData,
    login,
    legacyLogin,
    logout,
    showCaptchaState,
    removeCaptchaData,
  };
};
